import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useMe } from '@/api/hooks';
import { useAuthStore } from '@/store/auth';
import { useTranslation } from 'react-i18next';
import { Layout } from '@/components/Layout';
import { LazyRouteBoundary } from '@/components/LazyRouteBoundary';
import { LoadingPlaceholder } from '@/components/LoadingPlaceholder';
import { Login } from '@/pages/Login';
import { Translate } from '@/pages/Translate';
import { History } from '@/pages/History';
import { Settings } from '@/pages/Settings';
import { Lists } from '@/pages/Lists';
import { ListDetail } from '@/pages/ListDetail';
import { ItemListsPicker } from '@/pages/ItemListsPicker';

// Admin is a small minority of sessions, so its pages are split out of the
// entry bundle (and out of the service worker precache — see vite.config.ts).
// They share one chunk: an admin visiting one page usually visits several.
const AdminHub = lazy(async () => ({ default: (await import('@/pages/admin/Hub')).AdminHub }));
const AdminPrompts = lazy(async () => ({
  default: (await import('@/pages/admin/Prompts')).AdminPrompts,
}));
const AdminButtons = lazy(async () => ({
  default: (await import('@/pages/admin/Buttons')).AdminButtons,
}));
const AdminCheckboxes = lazy(async () => ({
  default: (await import('@/pages/admin/Checkboxes')).AdminCheckboxes,
}));
const AdminModels = lazy(async () => ({
  default: (await import('@/pages/admin/Models')).AdminModels,
}));
const AdminUsers = lazy(async () => ({
  default: (await import('@/pages/admin/Users')).AdminUsers,
}));
const AdminGlobals = lazy(async () => ({
  default: (await import('@/pages/admin/Globals')).AdminGlobals,
}));

function useHashToken() {
  const navigate = useNavigate();
  const location = useLocation();
  const setAccessToken = useAuthStore((s) => s.setAccessToken);

  useEffect(() => {
    if (!location.hash) return;
    const params = new URLSearchParams(location.hash.slice(1));
    const token = params.get('access_token');
    if (!token) return;
    setAccessToken(token);
    navigate(location.pathname + location.search, { replace: true });
  }, [location, navigate, setAccessToken]);
}

function useScrollToTopOnNavigate() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.accessToken);
  const me = useMe();
  const { t } = useTranslation();

  if (!token) return <Navigate to="/login" replace />;
  if (me.isLoading) {
    return <div className="p-6 text-center text-slate-500">{t('app.loading')}</div>;
  }
  if (me.isError) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const me = useMe();
  if (me.isLoading) return null;
  if (me.data?.role !== 'ADMIN') return <Navigate to="/" replace />;
  // Every admin route goes through here, so one pair covers the lazy chunk:
  // Suspense while it loads, the boundary when the fetch fails outright.
  return (
    <LazyRouteBoundary>
      <Suspense fallback={<LoadingPlaceholder />}>{children}</Suspense>
    </LazyRouteBoundary>
  );
}

export function App() {
  useHashToken();
  useScrollToTopOnNavigate();
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<Translate />} />
        <Route path="history" element={<History />} />
        <Route path="lists" element={<Lists />} />
        <Route path="lists/:listId" element={<ListDetail />} />
        <Route path="items/:translationId/lists" element={<ItemListsPicker />} />
        <Route path="settings" element={<Settings />} />
        <Route
          path="admin"
          element={
            <RequireAdmin>
              <AdminHub />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/prompts"
          element={
            <RequireAdmin>
              <AdminPrompts />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/buttons"
          element={
            <RequireAdmin>
              <AdminButtons />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/checkboxes"
          element={
            <RequireAdmin>
              <AdminCheckboxes />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/models"
          element={
            <RequireAdmin>
              <AdminModels />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/users"
          element={
            <RequireAdmin>
              <AdminUsers />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/globals"
          element={
            <RequireAdmin>
              <AdminGlobals />
            </RequireAdmin>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
